
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Information on the Plaid Dashboard institution insights and account activity',
  preloadImages: [{
    src: '/assets/img/docs/activity-logs.png',
    type: 'image/webp'
  }, {
    src: '/assets/img/docs/request-details.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Dashboard logs and troubleshooting' subtitle='Discover logging and troubleshooting information available in the Plaid Dashboard' mdxType="Header" />
    <h4 {...{
      "id": "logs-for-webhooks-link-and-api-requests"
    }}>{`Logs for webhooks, Link, and API requests`}</h4>
    <p>{`The Plaid Dashboard `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/logs"
      }}>{`Logs`}</a>{` page shows the past 14 days of API activity. Using the dashboard, you can see a record of all requests and responses, as well as all webhooks sent by the Plaid API, and all Link events. `}</p>
    <Image src='/assets/img/docs/activity-logs.png' alt="Plaid API logs showing request types, descriptions, institutions, environments, timestamps, and response codes in a dashboard." expandable={true} mdxType="Image" />
    <p>{`You can view the details of any request, response, webhook, or event, and view error information for any failed API request.`}</p>
    <Image src='/assets/img/docs/request-details.png' alt="Request and response details for failed API call: invalid public token expired. Error code 400, Halifax, Bank API, success rate 100%." expandable={true} mdxType="Image" />
    <h5 {...{
      "id": "link-analytics"
    }}>{`Link analytics`}</h5>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link-analytics"
      }}>{`Link analytics`}</a>{` page in the Dashboard shows a summary of Link conversion, along with top Link errors your users are experiencing and recommendations for increasing conversion. For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/best-practices"
      }}>{`Link conversion`}</a>{`.`}</p>
    <h4 {...{
      "id": "logs-for-billable-activity"
    }}>{`Logs for billable activity`}</h4>
    <p>{`The Plaid Dashboard `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/usage"
      }}>{`Usage Page`}</a>{` shows billable API usage for most Plaid products. Usage data is also available via the Plaid MCP server, allowing you to interact with this data via chat or an LLM agent. For more details, see the `}<a parentName="p" {...{
        "href": "https://plaid.com/blog/plaid-mcp-ai-assistant-claude/"
      }}>{`blog post on the Plaid Dashboard MCP server`}</a>{` and the `}<a parentName="p" {...{
        "href": "/docs/resources/mcp/"
      }}>{`Plaid Dashboard MCP server documentation`}</a>{`.`}</p>
    <h4 {...{
      "id": "logs-for-payment-initiation-transfer-and-signal"
    }}>{`Logs for Payment Initiation, Transfer, and Signal`}</h4>
    <p>{`The Payment Initiation (UK and Europe), Transfer, and Signal products also have their own logs in the Plaid Dashboard. If you are enabled for these products, links to view activity will appear in the Products menu within the dashboard. Using these logs, you can view the status and history of payment and transfer attempts, returns, and other product-specific information. For Payment Initiation and Transfer, this information is also available via the API; for more details, see the API documentation for `}<a parentName="p" {...{
        "href": "/docs/api/products/payment-initiation/"
      }}>{`Payment Initiation`}</a>{` and `}<a parentName="p" {...{
        "href": "/docs/api/products/transfer/"
      }}>{`Transfer`}</a>{`.`}</p>
    <h4 {...{
      "id": "audit-logs"
    }}>{`Audit logs`}</h4>
    <p>{`Audit logs of activity occurring in the Dashboard are available to admins on teams with Premium Support Packages. To learn more about upgrading to a Premium Support Package, contact your account manager.`}</p>
    <p>{`Dashboard audit logs include user identity, action type, IP address, and timestamps for core Dashboard actions, as well as for actions on the Identity Verification and Credit Dashboard pages. Audit logging is not yet available for other product-specific Dashboard pages.`}</p>
    <h4 {...{
      "id": "troubleshooting-institution-insights"
    }}>{`Troubleshooting institution insights`}</h4>
    <p>{`The Plaid Dashboard contains an `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/status"
      }}>{`Institutions`}</a>{` page, which allows you to view details and stats about institution connectivity over the past two weeks, as well as any recent downtime or special notes about the institution. Most of this information is also available via the API; for more information on programmatically retrieving institution insights, see `}<a parentName="p" {...{
        "href": "/docs/api/institutions/"
      }}>{`Institutions API endpoints`}</a>{`.`}</p>
    <Callout mdxType="Callout">
Because institution insights are based on your team's Production user activity, the Institutions page is only available to teams with Production access.
    </Callout>
    <p>{`You can search the Institutions page by institution name, institution ID, Item ID, request ID, access token, or Link session ID. `}</p>
    <h5 {...{
      "id": "institution-details"
    }}>{`Institution details`}</h5>
    <Image src='/assets/img/docs/institution-status-detail.png' expandable={true} mdxType="Image" />
    <p>{`Plaid displays both `}<strong parentName="p">{`New connections`}</strong>{` success rates (success adding new Items) and `}<strong parentName="p">{`Existing connection updates`}</strong>{` success rates (success refreshing data for existing Items). `}</p>
    <p>{`All success rates reflect the percentage of successful attempts to retrieve data. Plaid errors, institution errors, and user errors (e.g. failing to enter a correct password) are all combined when calculating the percentage of unsuccessful attempts.`}</p>
    <p>{`When viewing an institution, you can also see a breakdown of errors, including the number of your Items that the error impacts, as well as a list of all known issues related to that error. To subscribe to an issue and receive updates when it is resolved, click the "Follow" button on the upper right. Note that some known issues represent genuine user errors and cannot be resolved by Plaid. `}</p>
    <Image src='/assets/img/docs/error-detail.png' expandable={true} mdxType="Image" />
    <h5 {...{
      "id": "alerting"
    }}>{`Alerting`}</h5>
    <p>{`You can set up alerts to be notified of changes to the global Item add success rate of any institution. Alerts can be created directly from the institution's status page. You can also create and manage alerts from `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/team/notification-preferences"
      }}><strong parentName="a">{`Settings > Communications`}</strong></a>{`.  If you choose webhook-based alerting, the webhook that will be sent is the `}<a parentName="p" {...{
        "href": "/docs/api/institutions/#institution_status_alert_triggered"
      }}><inlineCode parentName="a">{`INSTITUTION_STATUS_ALERT_TRIGGERED`}</inlineCode></a>{` webhook.`}</p>
    <h5 {...{
      "id": "institution-migration-status"
    }}>{`Institution migration status`}</h5>
    <p>{`Within the Institutions page, you can use the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/status/migrations"
      }}>{`Migrations tab`}</a>{` to view the status of OAuth migrations. The Migrations tab shows all institutions with planned or current migrations and allows you to drill in to see the migration timeline and the impact on existing Items.`}</p>
    <Image src='/assets/img/docs/migration-status-detail.png' alt="Plaid migration status for a bank, in progress, type: Classic to API, timeline displayed." expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "troubleshooting-with-item-debugger"
    }}>{`Troubleshooting with Item Debugger`}</h4>
    <p>{`To view the status of a specific Item or Link session in the dashboard, you can use the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/debugger"
      }}>{`Item Debugger`}</a>{`. You can look up Items and Link sessions by `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`item_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, or `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{`. Troubleshooting information available includes error codes and suggested troubleshooting steps you can take to resolve any errors.`}</p>
    <p>{`Access to the Item Debugger is also available via Plaid's Dashboard MCP server, allowing you to troubleshoot Item details via LLM chat or an agent. For more details, see the `}<a parentName="p" {...{
        "href": "https://plaid.com/blog/plaid-mcp-ai-assistant-claude/"
      }}>{`blog post on the Plaid Dashboard MCP server`}</a>{` and the `}<a parentName="p" {...{
        "href": "/docs/resources/mcp/"
      }}>{`Plaid Dashboard MCP server documentation`}</a>{`.`}</p>
    <Image src='/assets/img/docs/item-debugger.png' alt="Item debugger showing a healthy item for institution Fifth Third Bank with an ID, transactions updated 6 minutes ago." mdxType="Image" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "logs-for-webhooks-link-and-api-requests",
        level: 4,
        title: "Logs for webhooks, Link, and API requests",
      },
{
        id: "logs-for-billable-activity",
        level: 4,
        title: "Logs for billable activity",
      },
{
        id: "logs-for-payment-initiation-transfer-and-signal",
        level: 4,
        title: "Logs for Payment Initiation, Transfer, and Signal",
      },
{
        id: "audit-logs",
        level: 4,
        title: "Audit logs",
      },
{
        id: "troubleshooting-institution-insights",
        level: 4,
        title: "Troubleshooting institution insights",
      },
{
        id: "troubleshooting-with-item-debugger",
        level: 4,
        title: "Troubleshooting with Item Debugger",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
